import { useEffect, useRef, useState } from 'react'
import Icon from '../../components/icons.jsx'
import { useApp } from '../../context/AppContext.jsx'
import * as api from '../../db/api.js'
import { safeHttpUrl } from '../../lib/safeUrl.js'

const SOURCE_OPTIONS = [
  { value: 'google_ads', label: 'Google Ads', color: '#4285f4' },
  { value: 'meta_ads', label: 'Meta Ads', color: '#1877f2' },
  { value: 'organico', label: 'Organico', color: '#34a853' },
  { value: 'manual', label: 'Manual', color: '#9e9e9e' },
]

const STATUS_LABELS = {
  visitantes: { label: 'Visitante', color: '#6e6e73' },
  formularios: { label: 'Formulario', color: '#0071e3' },
  whatsapp: { label: 'Click WhatsApp', color: '#34a853' },
  leads: { label: 'Lead', color: '#a259ff' },
  qualificado: { label: 'Qualificado', color: '#ff9f0a' },
  venda: { label: 'Venda', color: '#e85d04' },
  ganho: { label: 'Ganho ✓', color: '#30d158' },
  perdido: { label: 'Perdido', color: '#ff3b30' },
}

function ActivityTimeline({ activities, loading }) {
  const getStatusColor = (status) => STATUS_LABELS[status]?.color || '#6e6e73'

  if (loading) {
    return <div className="tl-empty">Carregando timeline...</div>
  }

  if (!activities?.length) {
    return <div className="tl-empty">Nenhuma atividade registrada</div>
  }

  return (
    <div className="lead-timeline">
      {activities.map((activity, index) => (
        <div key={activity.id || index} className="timeline-item">
          <div className="timeline-dot" style={{ background: getStatusColor(activity.to_status) }} />
          <div className="timeline-content">
            <div className="timeline-desc">{activity.description}</div>
            <div className="timeline-meta">
              <span>{activity.user_name}</span>
              <span>·</span>
              <span>
                {new Date(activity.created_at).toLocaleDateString('pt-BR', {
                  day: '2-digit',
                  month: 'short',
                  year: 'numeric',
                })}
              </span>
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}

// Valor digitado como texto ("1.500,50" ou "1500.5") <-> número. null = inválido.
function moneyToInput(value) {
  return value > 0 ? String(value).replace('.', ',') : ''
}

function parseMoney(text) {
  const raw = String(text ?? '').trim()
  if (!raw) return 0
  const normalized = raw.includes(',') ? raw.replace(/\./g, '').replace(',', '.') : raw
  const number = Number(normalized)
  return Number.isFinite(number) && number >= 0 ? number : null
}

const AD_CHANNELS = {
  lead_ads: 'Formulário de anúncio (Lead Ads)',
  click_to_whatsapp: 'Anúncio com botão de WhatsApp',
}
const PLATFORMS = { fb: 'Facebook', ig: 'Instagram', facebook: 'Facebook', instagram: 'Instagram' }

// Anúncio da Meta que gerou o lead (Lead Ads ou Click-to-WhatsApp).
function AdOrigin({ origin }) {
  const rows = [
    ['Canal', AD_CHANNELS[origin.channel] || origin.channel],
    ['Campanha', origin.campaign_name],
    ['Conjunto', origin.adset_name],
    ['Anúncio', origin.ad_name || origin.headline || origin.source_id],
    ['Formulário', origin.form_id],
    ['Plataforma', PLATFORMS[origin.platform] || origin.platform],
  ].filter(([, value]) => value)
  const url = safeHttpUrl(origin.source_url)
  return (
    <div className="lm-section">
      <div className="lm-section-title">Origem do anúncio (Meta)</div>
      {rows.map(([label, value]) => (
        <FieldRow key={label} label={label}><span>{value}</span></FieldRow>
      ))}
      {url && (
        <FieldRow label="Link">
          <a href={url} target="_blank" rel="noopener noreferrer">Abrir anúncio</a>
        </FieldRow>
      )}
    </div>
  )
}

function FieldRow({ label, children }) {
  return (
    <div className="lm-field-row">
      <label className="lm-field-label">{label}</label>
      <div className="lm-field-value">{children}</div>
    </div>
  )
}

function CloseActionBar({ lead, onOutcomeChange, onClose, toast }) {
  const [confirming, setConfirming] = useState(null)
  const [busy, setBusy] = useState(false)

  if (lead.outcome === 'ganho' || lead.outcome === 'perdido') {
    const isWon = lead.outcome === 'ganho'
    return (
      <div className="lm-close-bar">
        <div className={`lm-outcome-display ${isWon ? 'won' : 'lost'}`}>
          {isWon ? '✓ Fechado como ganho' : '✗ Perdido'}
          {isWon && lead.estimated_value > 0 ? ` · ${api.fmtBRL(lead.estimated_value)}` : ''}
        </div>
        <button
          className="lm-undo-btn"
          disabled={busy}
          onClick={async () => {
            setBusy(true)
            try {
              await onOutcomeChange(null)
              toast('Lead reativado no pipeline', 'info')
            } catch (err) {
              toast(err?.message || 'Erro ao desfazer', 'error')
            } finally {
              setBusy(false)
            }
          }}
        >
          ↩ Desfazer
        </button>
      </div>
    )
  }

  const currentStage = lead.stage || lead.status
  const canWin = currentStage === 'venda'

  const handleConfirm = async (outcome) => {
    if (outcome === 'ganho' && !(lead.estimated_value > 0)) {
      toast('Defina o valor do negocio antes de fechar como ganho', 'error')
      return
    }

    setBusy(true)
    try {
      await onOutcomeChange(outcome)
      toast(outcome === 'ganho' ? 'Venda registrada com sucesso' : 'Lead marcado como perdido', outcome === 'ganho' ? 'success' : 'info')
      onClose()
    } catch (err) {
      toast(err?.message || 'Erro ao atualizar', 'error')
    } finally {
      setBusy(false)
      setConfirming(null)
    }
  }

  return (
    <div className="lm-close-bar">
      {confirming === 'perdido' ? (
        <div className="lm-confirm-row">
          <span className="lm-confirm-text">
            Marcar como perdido na etapa <strong>{currentStage}</strong>?
          </span>
          <button className="lm-btn lm-btn-ghost" onClick={() => setConfirming(null)}>
            Nao
          </button>
          <button className="lm-btn lm-btn-danger" onClick={() => handleConfirm('perdido')} disabled={busy}>
            {busy ? '...' : 'Confirmar'}
          </button>
        </div>
      ) : confirming === 'ganho' ? (
        <div className="lm-confirm-row">
          <span className="lm-confirm-text">
            Fechar como ganho
            {lead.estimated_value > 0 ? (
              <strong> · {api.fmtBRL(lead.estimated_value)}</strong>
            ) : (
              <span style={{ color: 'var(--red)', fontSize: 12 }}> · defina o valor primeiro</span>
            )}
          </span>
          <button className="lm-btn lm-btn-ghost" onClick={() => setConfirming(null)}>
            Cancelar
          </button>
          <button className="lm-btn lm-btn-won" onClick={() => handleConfirm('ganho')} disabled={busy}>
            {busy ? '...' : 'Confirmar ganho'}
          </button>
        </div>
      ) : (
        <>
          <button className="lm-action-btn lm-action-lost" onClick={() => setConfirming('perdido')}>
            <Icon n="close" s={14} /> Marcar perdido
          </button>
          <button
            className="lm-action-btn lm-action-won"
            onClick={() =>
              canWin ? setConfirming('ganho') : toast('O lead precisa estar em Venda para fechar como ganho', 'error')
            }
            style={!canWin ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
          >
            <Icon n="check" s={14} />
            {canWin ? 'Fechar como ganho' : 'Ganho (somente em Venda)'}
          </button>
        </>
      )}
    </div>
  )
}

export default function LeadModal({ lead: initialLead, onClose, onStatusChange, onSave }) {
  const { toast, activeClient } = useApp()
  const activeClientId = activeClient?.id ?? null
  const [lead, setLead] = useState(initialLead)
  const [activities, setActivities] = useState([])
  const [actLoading, setActLoading] = useState(true)
  const [editing, setEditing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState({ ...initialLead })
  const [valueInput, setValueInput] = useState(moneyToInput(initialLead?.estimated_value))
  const overlayRef = useRef(null)

  // Fase 5 (B6): guard defensivo — se o cliente ativo mudar enquanto o modal
  // está aberto, fechamos. O pai (LeadAnalysis) já zera `selectedLead` em
  // troca de cliente, mas mantemos o guard local para futuro reuso do modal
  // em outras páginas. `lastClientIdRef` evita disparar no mount inicial
  // (activeClientId resolve do null para um valor real) e em renders onde
  // `onClose` muda de identidade sem que o cliente tenha mudado.
  const lastClientIdRef = useRef(activeClientId)
  useEffect(() => {
    const prev = lastClientIdRef.current
    lastClientIdRef.current = activeClientId
    if (prev !== null && activeClientId !== null && prev !== activeClientId) {
      onClose()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeClientId])

  useEffect(() => {
    setLead(initialLead)
    setForm({ ...initialLead })
    setValueInput(moneyToInput(initialLead?.estimated_value))
  }, [initialLead])

  // Fase 5 (B6): versioning protege contra resposta tardia de getLeadActivities
  // após troca de cliente (o pai fecha o modal, mas a request anterior pode
  // chegar com 200 + activities do cliente A).
  const activitiesGenRef = useRef(0)
  useEffect(() => {
    if (!lead?.id) return
    const myGen = ++activitiesGenRef.current
    setActLoading(true)
    api
      .getLeadActivities(lead.id)
      .then((data) => {
        if (myGen !== activitiesGenRef.current) return
        setActivities(data)
      })
      .catch(() => {
        if (myGen !== activitiesGenRef.current) return
        setActivities([])
      })
      .finally(() => {
        if (myGen === activitiesGenRef.current) setActLoading(false)
      })
  }, [lead?.id])

  useEffect(() => {
    const onKey = (event) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const handleOverlayClick = (event) => {
    if (event.target === overlayRef.current) {
      onClose()
    }
  }

  const handleSave = async () => {
    const estimatedValue = parseMoney(valueInput)
    if (estimatedValue === null) {
      toast('Valor do negocio invalido', 'error')
      return
    }
    setSaving(true)
    try {
      const payload = { ...form, estimated_value: estimatedValue }
      await api.updateLead(lead.id, payload)
      const updated = { ...lead, ...payload }
      setLead(updated)
      setEditing(false)
      toast('Lead atualizado', 'success')
      onSave?.(updated)
      const nextActivities = await api.getLeadActivities(lead.id)
      setActivities(nextActivities)
    } catch (err) {
      toast(err?.message || 'Erro ao salvar', 'error')
    } finally {
      setSaving(false)
    }
  }

  const handleOutcomeChange = async (outcome) => {
    await onStatusChange?.(lead.id, { outcome })
    const updated = { ...lead, outcome, status: outcome || lead.stage }
    setLead(updated)
    const nextActivities = await api.getLeadActivities(lead.id)
    setActivities(nextActivities)
  }

  const srcInfo = SOURCE_OPTIONS.find((item) => item.value === lead.source) || SOURCE_OPTIONS[3]
  const statusInfo =
    STATUS_LABELS[lead.outcome] || STATUS_LABELS[lead.stage] || STATUS_LABELS[lead.status] || STATUS_LABELS.visitantes

  const formatDate = (value) => {
    if (!value) return '—'
    return new Date(value).toLocaleDateString('pt-BR')
  }

  const isFinished = lead.outcome === 'ganho' || lead.outcome === 'perdido'

  return (
    <div className="lm-overlay" ref={overlayRef} onClick={handleOverlayClick}>
      <div className="lm-panel">
        <div className="lm-header">
          <div className="lm-header-left">
            <div className="lm-avatar" style={{ background: isFinished ? (lead.outcome === 'ganho' ? '#30d158' : '#ff3b30') : 'var(--black)' }}>
              {(lead.name || '?')[0]}
            </div>
            <div style={{ minWidth: 0 }}>
              <div className="lm-name">
                {editing ? (
                  <input
                    className="lm-input lm-name-input"
                    value={form.name || ''}
                    onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
                  />
                ) : (
                  lead.name || 'Sem nome'
                )}
              </div>
              <div className="lm-header-meta">
                <span
                  className="lm-status-badge"
                  style={{
                    background: `${statusInfo.color}22`,
                    color: statusInfo.color,
                    border: `1px solid ${statusInfo.color}44`,
                  }}
                >
                  {statusInfo.label}
                </span>
                {lead.is_qualified ? <span className="lm-qualified-badge">✓ Qualificado</span> : null}
                <span style={{ color: srcInfo.color, fontSize: 12, fontWeight: 600 }}>{srcInfo.label}</span>
              </div>
            </div>
          </div>
          <div className="lm-header-actions">
            {lead.whatsapp_link ? (
              <button className="lm-icon-btn" title="WhatsApp" onClick={() => window.open(lead.whatsapp_link, '_blank')}>
                <Icon n="phone" s={16} />
              </button>
            ) : null}
            {!editing ? (
              <button className="lm-icon-btn" title="Editar" onClick={() => {
                setEditing(true)
                setForm({ ...lead })
                setValueInput(moneyToInput(lead.estimated_value))
              }}>
                <Icon n="settings" s={16} />
              </button>
            ) : (
              <>
                <button className="lm-btn lm-btn-ghost" onClick={() => setEditing(false)}>
                  Cancelar
                </button>
                <button className="lm-btn lm-btn-primary" onClick={handleSave} disabled={saving}>
                  {saving ? 'Salvando...' : 'Salvar'}
                </button>
              </>
            )}
            <button className="lm-icon-btn" onClick={onClose}>
              <Icon n="close" s={18} />
            </button>
          </div>
        </div>

        {!editing ? (
          <CloseActionBar lead={lead} onOutcomeChange={handleOutcomeChange} onClose={onClose} toast={toast} />
        ) : null}

        <div className="lm-body">
          <div className="lm-col lm-col-main">
            <div className="lm-section">
              <div className="lm-section-title">Identificacao e contato</div>
              <FieldRow label="Nome">
                {editing ? (
                  <input
                    className="lm-input"
                    value={form.name || ''}
                    onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
                  />
                ) : (
                  <span>{lead.name || '—'}</span>
                )}
              </FieldRow>
              <FieldRow label="Telefone">
                {editing ? (
                  <input
                    className="lm-input"
                    value={form.phone || ''}
                    onChange={(event) => setForm((prev) => ({ ...prev, phone: event.target.value }))}
                  />
                ) : (
                  <span>{lead.phone || '—'}</span>
                )}
              </FieldRow>
              <FieldRow label="Valor do negocio">
                {editing ? (
                  <input
                    className="lm-input"
                    inputMode="decimal"
                    placeholder="0,00"
                    value={valueInput}
                    onChange={(event) => setValueInput(event.target.value)}
                  />
                ) : (
                  <span>{lead.estimated_value > 0 ? api.fmtBRL(lead.estimated_value) : '—'}</span>
                )}
              </FieldRow>
              <FieldRow label="WhatsApp ID">
                <span>{lead.id_user_whats || '—'}</span>
              </FieldRow>
              <FieldRow label="Email">
                {editing ? (
                  <input
                    className="lm-input"
                    value={form.email || ''}
                    onChange={(event) => setForm((prev) => ({ ...prev, email: event.target.value }))}
                  />
                ) : (
                  <span>{lead.email || '—'}</span>
                )}
              </FieldRow>
            </div>

            <div className="lm-section">
              <div className="lm-section-title">Comportamento no site</div>
              <FieldRow label="Visitas">
                <span style={{ fontWeight: 700 }}>{lead.visit_count || 0} acessos</span>
              </FieldRow>
              <FieldRow label="Preencheu formulario">
                <span>{lead.filled_form ? 'Sim' : 'Nao'}</span>
              </FieldRow>
              <FieldRow label="Entrou em contato">
                <span>{lead.contacted ? 'Sim' : 'Nao'}</span>
              </FieldRow>
              <FieldRow label="Clicou WhatsApp">
                <span>{lead.clicked_whats ? 'Sim' : 'Nao'}</span>
              </FieldRow>
              <FieldRow label="Mensagem inicial">
                <span style={{ fontSize: 12, fontStyle: 'italic', color: 'var(--sec)' }}>
                  {lead.initial_message || '—'}
                </span>
              </FieldRow>
            </div>

            {lead.ad_origin && <AdOrigin origin={lead.ad_origin} />}

            <div className="lm-section">
              <div className="lm-section-title">Origem e marketing</div>
              <FieldRow label="Campanha">
                <span className="lm-utm">{lead.utm_campaign || '—'}</span>
              </FieldRow>
              <FieldRow label="Keyword">
                <span className="lm-utm">{lead.keyword || '—'}</span>
              </FieldRow>
              <FieldRow label="Source">
                <span className="lm-utm">{lead.utm_source || '—'}</span>
              </FieldRow>
              <FieldRow label="Content">
                <span className="lm-utm">{lead.utm_content || '—'}</span>
              </FieldRow>
              <FieldRow label="Referer">
                <span className="lm-utm" style={{ fontSize: 10 }}>
                  {lead.referer || 'Direto'}
                </span>
              </FieldRow>
              <FieldRow label="Pagina completa">
                <span className="lm-utm" style={{ fontSize: 10, wordBreak: 'break-all' }}>
                  {lead.full_page || '—'}
                </span>
              </FieldRow>
            </div>

            <div className="lm-section">
              <div className="lm-section-title">Geolocalizacao</div>
              <FieldRow label="Localizacao">
                <span>{lead.location || '—'}</span>
              </FieldRow>
            </div>

            <div className="lm-section">
              <div className="lm-section-title">Datas cruciais</div>
              <FieldRow label="Acesso site">
                <span>{formatDate(lead.first_access)}</span>
              </FieldRow>
              <FieldRow label="Entrou no funil">
                <span>{formatDate(lead.funnel_entry)}</span>
              </FieldRow>
            </div>
          </div>

          <div className="lm-col lm-col-timeline">
            <div className="lm-section-title" style={{ marginBottom: 16 }}>
              <Icon n="clock" s={14} c="var(--sec)" /> Activity Timeline
            </div>
            <ActivityTimeline activities={activities} loading={actLoading} />
          </div>
        </div>
      </div>
    </div>
  )
}
